import { useSetAtom } from 'jotai';
import type { RxDocument } from 'rxdb';
import type { CSSProperties } from 'react';
import { Editor as TiptapEditor } from '@tiptap/react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { Editor } from '@/components/editor';
import { LlmChat } from '@/features/llm-chat';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Button } from '@flintmint/web-shared/components/button';
import { BlockEditorSidebar } from './parts/block-editor-sidebar';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { SimpleDocumentHeader } from '@/components/simple-document-header';
import { SuggestionsNavigation } from '@/components/suggestion-navigation';
import { OverlaySidebar } from '@flintmint/web-shared/components/overlay-sidebar';

import { useApiRequestState } from '@/hooks/api';
import { useAutosave } from '@/hooks/use-autosave';
import { useSyncedEditorContent, withSyncedEditorContentWrite } from '@/hooks/use-synced-editor-content';
import { guardOwnWrite, type MutationOptions } from '@/hooks/rxdb-base';
import { useItemActions } from '@/hooks/use-item-actions';
import { useLeaveOnArchived } from '@/hooks/use-leave-on-archived';
import { useLLMAgent, useLlmEditorScope } from '@/hooks/llm';
import { useBlockEditorState } from '@/hooks/use-block-editor-state';
import {
  useAgentRunUiState,
  useReviewActions,
  useExternalReviewResolution,
  toPlainPendingReview,
  type PendingReview,
  type AgentEditTarget,
  type PersistedDocumentPatch,
} from '@/hooks/llm/use-llm-agent';
import { useBlockDuplicate, useBlockUpsert, useUpdateUserPreferences, useUserPreferences } from '@/hooks/rxdb-models';

import { sidebarOverlayAtom } from '@/state';
import { copyToClipboard } from '@/utils/helper-utils';
import { getBlockNodeByInstanceId, isEditorAlive, isEqualWithHTML } from '@/utils/editor-utils';

import type { BlockContext } from '@/constants/types';
import type { Block } from '@flintmint/core-shared/rxdb';
import type { BlockNodeWithPos } from '@/utils/editor-utils';

import styles from './block-editor-overlay.module.scss';

export const BlockEditorOverlay = (props: BlockEditorOverlayProps) => {
  const { open, blockId, requestId, onClose } = props;

  const contentRef = useRef<BlockEditorOverlayContentHandle | null>(null);

  const handleOverlayClose = useCallback(() => {
    const closeHandler = contentRef.current?.requestClose;

    if (closeHandler) {
      return closeHandler();
    }

    onClose();
  }, [onClose]);

  return (
    <OverlaySidebar
      open={open}
      onClose={handleOverlayClose}
      loader={
        <div className={styles.overlayWarmup}>
          <Loader absolute />
        </div>
      }
    >
      <BlockEditorOverlayContent ref={contentRef} key={requestId ?? blockId} {...props} />
    </OverlaySidebar>
  );
};

const BlockEditorOverlayContent = forwardRef<BlockEditorOverlayContentHandle, BlockEditorOverlayProps>(
  function BlockEditorOverlayContent(props, ref) {
    const {
      open,
      requestId,
      blockId,
      onClose,
      context,
      context: { note, position, blockInstanceId, autoApplyChanges, noteId, editor: noteEditor } = {},
    } = props;

    const [editor, setEditor] = useState<TiptapEditor | null>(null);
    const [didInitNormalize, setDidInitNormalize] = useState(false);
    const [applyChangesToNote, setApplyChangesToNote] = useState(true);
    const [persistedBlock, setPersistedBlock] = useState<RxDocument<Block> | undefined>(undefined);
    const [pendingPrompt, setPendingPrompt] = useState<QueuedPrompt | null>(null);
    const [hideWarning, setHideWarning] = useState<boolean>(false);

    const setSidebarOverlay = useSetAtom(sidebarOverlayAtom);
    const isMountedRef = useRef(false);
    const isOpenRef = useRef(open);

    const { preferences } = useUserPreferences();
    const { mutate: updateUserPreferences } = useUpdateUserPreferences();

    const attachedBlockSyncStateByRequestId = useRef(new Map<string, AttachedBlockSyncState>());

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      isOpenRef.current = open;
    }, [open]);

    const {
      name,
      block,
      ready,
      tagIds,
      content,
      setName,
      setTagIds,
      savedState,
      commitSavedState,
      onEditorUpdate,
      blockInstructions,
      onInstructionCreate,
      onInstructionDelete,
      onInstructionEnabledChange,
    } = useBlockEditorState({
      open,
      blockId,
      draftId: requestId,
      context,
    });

    const activeBlock = block ?? persistedBlock;
    const editorDocumentId = requestId ?? activeBlock?.id ?? blockId;
    const documentKey = activeBlock?.id ?? editorDocumentId ?? 'new';

    const { configureDestructiveOptions } = useItemActions();
    const duplicateBlockMutation = useBlockDuplicate();

    const { mutate: upsertBlock } = useBlockUpsert(activeBlock);

    const activeBlockContent = useMemo(() => (activeBlock ? (activeBlock.toJSON() as Block).content : undefined), [activeBlock]);
    const getLiveEditor = useCallback(() => (isEditorAlive(editor) ? editor : null), [editor]);
    const {
      hasUserEdits,
      getLiveContent: getLiveBlockContent,
      markSynced,
    } = useSyncedEditorContent({
      documentKey,
      editorContent: activeBlockContent ?? content,
      getLiveEditor,
      fallbackContent: content,
      documentContent: activeBlockContent,
    });

    const hasNoteContext = !!noteEditor && !!(noteId || note);
    const didPermanentlyDismissWarning = preferences?.originalBlockEditWarning;
    const llmRequestId = requestId ?? 'block-editor-overlay';
    const { editorState, isLoading: isAgentRunLoading } = useAgentRunUiState(llmRequestId);
    const isAgentWriting = editorState === 'agent_writing';
    const documentPendingReview = (activeBlock?.pendingReview ?? null) as PendingReview | null;

    const getAttachedBlockSyncState = useCallback(
      () => attachedBlockSyncStateByRequestId.current.get(llmRequestId) ?? null,
      [llmRequestId]
    );

    const setAttachedBlockSyncState = useCallback(
      (nextSyncState: AttachedBlockSyncState | null) => {
        if (nextSyncState) {
          attachedBlockSyncStateByRequestId.current.set(llmRequestId, nextSyncState);
          return;
        }

        attachedBlockSyncStateByRequestId.current.delete(llmRequestId);
      },
      [llmRequestId]
    );

    const syncAttachedNoteBlock = useCallback(
      (blockData: Block, options: { applyChangesToNote?: boolean } = {}) => {
        if (!hasNoteContext || !noteEditor) return;

        const attachedBlock = getAttachedBlockSyncState();
        const shouldApplyChangesToNote = attachedBlock?.applyChangesToNote ?? options.applyChangesToNote ?? applyChangesToNote;

        if (!shouldApplyChangesToNote) return;

        if (attachedBlock) {
          noteEditor
            .chain()
            .replaceBlockWithNew(attachedBlock.instanceId, attachedBlock.position, blockData, { preserveSelection: true })
            .run();

          const updatedBlock =
            noteEditor.state?.doc && noteEditor.storage?.blockNode
              ? getBlockNodeByInstanceId(noteEditor, attachedBlock.instanceId)
              : null;
          if (updatedBlock) {
            setAttachedBlockSyncState({
              instanceId: attachedBlock.instanceId,
              position: updatedBlock.startPos,
              applyChangesToNote: attachedBlock.applyChangesToNote,
            });
          }
        } else {
          const existingInstanceIds = getBlockInstanceIds(noteEditor);
          noteEditor.chain().insertBlock(blockData).run();

          const insertedBlock = findInsertedBlockNode(noteEditor, existingInstanceIds, blockData.id);
          if (insertedBlock) {
            setAttachedBlockSyncState({
              instanceId: insertedBlock.node.attrs.instanceId,
              position: insertedBlock.startPos,
              applyChangesToNote: shouldApplyChangesToNote,
            });
          }
        }
      },
      [applyChangesToNote, getAttachedBlockSyncState, hasNoteContext, noteEditor, setAttachedBlockSyncState]
    );

    const persistBlock = useCallback(
      async (payload: Partial<Block>, options: { syncAttachedNoteBlock?: boolean; guarded?: boolean } = {}) => {
        const savedBlock = await guardOwnWrite(activeBlock, (ifUnmodifiedSince) => {
          let mutationOptions: MutationOptions | undefined;
          if (!activeBlock && requestId) {
            mutationOptions = { id: requestId };
          } else if (options.guarded) {
            mutationOptions = { ifUnmodifiedSince };
          }
          return upsertBlock(payload, mutationOptions);
        });
        const serializedBlock = savedBlock.toJSON() as Block;

        if (isMountedRef.current && isOpenRef.current) {
          setPersistedBlock(savedBlock);
          await commitSavedState(serializedBlock);
        }
        if (options.syncAttachedNoteBlock ?? true) {
          syncAttachedNoteBlock(serializedBlock);
        }

        return savedBlock;
      },
      [activeBlock, commitSavedState, requestId, syncAttachedNoteBlock, upsertBlock]
    );

    const persistCurrentBlock = useCallback(
      async () =>
        persistBlock({
          name,
          tagIds,
          content,
          instructions: blockInstructions || {},
          pendingReview: activeBlock?.pendingReview ?? null,
        }),
      [activeBlock?.pendingReview, blockInstructions, content, name, persistBlock, tagIds]
    );

    const persistBlockDocumentPatch = useCallback(
      async (patch: PersistedDocumentPatch) => {
        const shouldSyncAttachedNoteBlock = patch.content !== undefined;

        if (activeBlock) {
          await persistBlock(
            {
              ...(patch.content !== undefined && { content: patch.content }),
              ...(patch.pendingReview !== undefined && { pendingReview: patch.pendingReview }),
            },
            { syncAttachedNoteBlock: shouldSyncAttachedNoteBlock }
          );
          return;
        }

        await persistBlock(
          {
            name,
            tagIds,
            instructions: blockInstructions || {},
            content: patch.content !== undefined ? patch.content : savedState.content,
            pendingReview: patch.pendingReview !== undefined ? patch.pendingReview : null,
          },
          {
            syncAttachedNoteBlock: shouldSyncAttachedNoteBlock,
          }
        );
      },
      [activeBlock, blockInstructions, name, persistBlock, savedState.content, tagIds]
    );

    const pendingReview = useMemo(
      () => (documentPendingReview ? toPlainPendingReview(documentPendingReview) : null),
      [documentPendingReview]
    );
    const hasPendingReview = (pendingReview?.suggestions.length ?? 0) > 0;

    useExternalReviewResolution({
      requestId: llmRequestId,
      documentKey,
      hasPendingReview,
      editor,
    });

    const review = useReviewActions({
      requestId: llmRequestId,
      editor,
      pendingReview,
      persistDocumentPatch: persistBlockDocumentPatch,
    });
    const hasLLMSuggestions = (review?.suggestions.length ?? 0) > 0;

    const { resetMutationState } = useApiRequestState(llmRequestId);

    const {
      chatMessages,
      sendChatMessage,
      retryLastMessage,
      cancel: cancelAgentRequest,
      handleSuggestionDecision,
    } = useLLMAgent({
      requestId: llmRequestId,
      editor: editor!,
      document: activeBlock,
      persistDocumentPatch: persistBlockDocumentPatch,
    });

    // BlockEditorOverlayContent is skipped by the React Compiler (ref write in render), so memoize by hand to keep Editor's memo effective.
    const renderPromptBox = useCallback(
      ({ style }: { style: CSSProperties }) =>
        hasLLMSuggestions &&
        !!editor &&
        !!review && (
          <SuggestionsNavigation editor={editor} review={review} onAllResolved={handleSuggestionDecision} style={style} />
        ),
      [editor, handleSuggestionDecision, hasLLMSuggestions, review]
    );

    const { hasSelection } = useLlmEditorScope(editor);
    const canWriteLiveContent = editorState === 'interactive' && !hasPendingReview;

    const {
      isSaving: isAutosaving,
      hasChanges: autosaveHasChanges,
      flush: flushAutosave,
    } = useAutosave({
      // Block content stays out of `data`: diffing it here costs a full serialize and reparse on
      // every render. The editor reports whether it holds unsaved content instead.
      initialData: {
        name: savedState.name,
        tagIds: savedState.tagIds,
        instructions: savedState.instructions,
      },
      data: {
        name,
        tagIds,
        instructions: blockInstructions || {},
      },
      hasContentChanges: canWriteLiveContent && hasUserEdits,
      onSave: (nextBlockData) => {
        const liveContent = getLiveBlockContent();
        const willWriteLiveContent = canWriteLiveContent && (hasUserEdits || !activeBlock);

        // Compared against the stored revision, not the rendered one: an agent rename or an earlier save
        // may already hold these values.
        const payload = pickChangedMetadata(nextBlockData, activeBlock?.getLatest());
        if (willWriteLiveContent) {
          payload.content = liveContent;
        }

        if (Object.keys(payload).length === 0) {
          return Promise.resolve();
        }

        const write = persistBlock(payload, willWriteLiveContent && activeBlock ? { guarded: true } : undefined);

        return withSyncedEditorContentWrite(write, {
          willWrite: willWriteLiveContent,
          liveContent,
          markSynced,
        });
      },
      enabled: open && ready && didInitNormalize,
      resetKey: `${editorDocumentId}:${activeBlock?.updatedAt ?? ''}:${activeBlock?.name ?? ''}`,
      flushOnLeave: true,
    });

    const submitBlockPrompt = useCallback(
      async (text: string, options?: { editTarget?: AgentEditTarget }) => {
        if (activeBlock && !(autosaveHasChanges || isAutosaving)) {
          void sendChatMessage(text, options);
          return;
        }

        const flushResult = await flushAutosave();

        if (!activeBlock && !flushResult.didSave) {
          await persistCurrentBlock();
        }

        setPendingPrompt({ text, options });
      },
      [activeBlock, autosaveHasChanges, flushAutosave, isAutosaving, persistCurrentBlock, sendChatMessage]
    );

    const handleCloseRef = useRef<() => void | Promise<void>>(() => {});

    const closeOverlay = useCallback(
      (options: { preserveAttachedBlock?: boolean } = {}) => {
        onClose();
        if (!options.preserveAttachedBlock) {
          setAttachedBlockSyncState(null);
          resetMutationState(llmRequestId);
        }
      },
      [llmRequestId, onClose, resetMutationState, setAttachedBlockSyncState]
    );

    const handleClose = useCallback(async () => {
      try {
        await flushAutosave();
      } catch (error) {
        console.error('Failed to save block before closing:', error);
        return;
      }

      closeOverlay({ preserveAttachedBlock: isAgentWriting || isAgentRunLoading });
    }, [closeOverlay, flushAutosave, isAgentRunLoading, isAgentWriting]);
    handleCloseRef.current = handleClose;

    useImperativeHandle(
      ref,
      () => ({
        requestClose: handleClose,
      }),
      [handleClose]
    );

    useEffect(() => {
      if (!open) return;

      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        void handleCloseRef.current();
      };

      window.addEventListener('keydown', onKeyDown);

      return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    useEffect(() => {
      if (!open) return;

      setApplyChangesToNote(true);
      setAttachedBlockSyncState(
        blockInstanceId && typeof position === 'number'
          ? { instanceId: blockInstanceId, position, applyChangesToNote: true }
          : null
      );
      setPersistedBlock(undefined);
      setPendingPrompt(null);
      setDidInitNormalize(false);
      setHideWarning(false);
    }, [blockInstanceId, open, position, setAttachedBlockSyncState]);

    useEffect(() => {
      if (!pendingPrompt || !activeBlock) return;

      setPendingPrompt(null);
      void sendChatMessage(pendingPrompt.text, pendingPrompt.options);
    }, [activeBlock, pendingPrompt, sendChatMessage]);

    useLeaveOnArchived(activeBlock, () => {
      void handleCloseRef.current();
    });

    return (
      <>
        <div className={styles.blockEditorOverlay} data-testid="block-editor-overlay">
          {blockId && !block ? (
            <Loader absolute data-testid="loader" />
          ) : (
            <>
              <SimpleDocumentHeader
                type="blocks"
                editor={editor}
                data={activeBlock ?? undefined}
                tagIds={tagIds}
                documentName={name}
                withDivider={false}
                onClose={handleClose}
                onNameChange={setName}
                className={styles.header}
                onTagsUpdate={setTagIds}
                menu={[
                  {
                    icon: 'copy-02',
                    name: 'Copy Contents',
                    onClick: () => copyToClipboard({ content, textContent: editor?.getText() }),
                  },
                  ...configureDestructiveOptions(activeBlock ? [{ ...(activeBlock.toJSON() || {}), type: 'blocks' }] : [], {
                    onDone: onClose,
                  }).map((option) => ({
                    disabled: !activeBlock,
                    ...option,
                  })),
                ]}
                actions={
                  !!activeBlock && (
                    <Button
                      color="light"
                      onClick={async () => {
                        try {
                          await flushAutosave();
                        } catch (error) {
                          console.error('Failed to save block before cloning:', error);
                          return;
                        }

                        await duplicateBlockMutation.mutate(activeBlock.id, {
                          onSuccess: (duplicatedData: RxDocument<Block>) => {
                            setSidebarOverlay({
                              type: 'blocks',
                              blockId: duplicatedData.id,
                              context: {
                                skipOriginalBlockWarning: true,
                              },
                            });
                          },
                        });
                      }}
                    >
                      Clone and edit
                    </Button>
                  )
                }
              />
              <Collapse
                open={!!activeBlock && !context?.skipOriginalBlockWarning && !didPermanentlyDismissWarning && !hideWarning}
                className={styles.warningHolder}
              >
                <div className={styles.warning} data-testid="original-block-warning">
                  <div>
                    <Icon data-testid="icon" type="information-circle" size={16} className={styles.warningIcon} />
                  </div>
                  <div>
                    <span className="ellipsis-text">
                      You're editing the original block right now. You can <strong>clone</strong> it and then make changes to keep
                      the original intact.
                    </span>
                    <button
                      className={styles.dontShowAgain}
                      onClick={() => {
                        setHideWarning(true);
                        updateUserPreferences({ originalBlockEditWarning: true })?.then(() => {});
                      }}
                    >
                      Don't show again
                    </button>
                  </div>
                  <button
                    className={styles.closeIcon}
                    onClick={() => {
                      setHideWarning(true);
                    }}
                  >
                    <Icon type="cancel-01" data-testid="icon-close" size={16} />
                  </button>
                </div>
              </Collapse>
              <div className={styles.blockEditorHolder}>
                <div className={styles.blockEditorColumn}>
                  {ready && (
                    <Editor
                      autofocus
                      cacheable
                      selfScrollable
                      editorContentTestId="block-editor-content"
                      canSync={!hasUserEdits}
                      onContentSynced={markSynced}
                      content={content}
                      editorType="block"
                      documentId={editorDocumentId}
                      pendingReview={pendingReview}
                      didInitNormalize={didInitNormalize}
                      setDidInitNormalize={setDidInitNormalize}
                      setEditor={setEditor}
                      onUpdate={onEditorUpdate}
                      llmRequestId={llmRequestId}
                      persistDocumentPatch={persistBlockDocumentPatch}
                      placeholder="Start your block content"
                      promptBox={renderPromptBox}
                    />
                  )}
                </div>
                <BlockEditorSidebar
                  attachedToNote={!!noteId}
                  autoApplyChanges={!!autoApplyChanges}
                  applyChangesToNote={applyChangesToNote}
                  onApplyChangesToNote={() => {
                    const nextApplyChangesToNote = !applyChangesToNote;
                    setApplyChangesToNote(nextApplyChangesToNote);

                    const attachedBlock = getAttachedBlockSyncState();
                    if (attachedBlock) {
                      setAttachedBlockSyncState({
                        ...attachedBlock,
                        applyChangesToNote: nextApplyChangesToNote,
                      });
                    }

                    if (nextApplyChangesToNote && !(autosaveHasChanges || isAutosaving) && activeBlock) {
                      syncAttachedNoteBlock(activeBlock.toJSON() as Block, { applyChangesToNote: nextApplyChangesToNote });
                    }
                  }}
                  blockInstructions={blockInstructions}
                  onInstructionCreate={onInstructionCreate}
                  onInstructionDelete={onInstructionDelete}
                  onInstructionEnabledChange={onInstructionEnabledChange}
                />
              </div>
            </>
          )}
        </div>
        {/*
          The chat mounts with the overlay and outlives the document: it must stay up while the block
          is still loading (or never resolves — stale deep link, deleted elsewhere) and after a Delete.
        */}
        {open && (
          <LlmChat
            editor={editor}
            requestId={llmRequestId}
            documentKind="block"
            dockColumn="block-editor"
            chatMessages={chatMessages}
            hasSelection={hasSelection}
            onSubmitPrompt={submitBlockPrompt}
            cancelAgentRequest={cancelAgentRequest}
            onRetryNetworkError={retryLastMessage}
          />
        )}
      </>
    );
  }
);

export interface BlockEditorOverlayProps {
  open: boolean;
  blockId?: string;
  requestId?: string;
  onClose: () => void;
  context?: BlockContext;
}

type BlockEditorOverlayContentHandle = {
  requestClose: () => void | Promise<void>;
};

type AttachedBlockSyncState = {
  instanceId: string;
  position: number;
  applyChangesToNote: boolean;
};

type QueuedPrompt = {
  text: string;
  options?: {
    editTarget?: AgentEditTarget;
  };
};

const blockMetadataKeys = ['name', 'tagIds', 'instructions'] as const;

function pickChangedMetadata(metadata: Record<string, any>, storedBlock: Block | undefined): Partial<Block> {
  const storedMetadata: Record<string, unknown> | undefined = storedBlock && {
    name: storedBlock.name,
    tagIds: storedBlock.tagIds,
    instructions: storedBlock.instructions ?? {},
  };

  return Object.fromEntries(
    blockMetadataKeys
      .filter((key) => !storedMetadata || !isEqualWithHTML({ [key]: metadata[key] }, { [key]: storedMetadata[key] }))
      .map((key) => [key, metadata[key]])
  );
}

function getBlockInstanceIds(editor: TiptapEditor) {
  const instanceIds = new Set<string>();

  editor.state?.doc?.descendants((node) => {
    if (node.type.name === 'blockNode' && node.attrs.instanceId) {
      instanceIds.add(node.attrs.instanceId);
    }
    return true;
  });

  return instanceIds;
}

function findInsertedBlockNode(editor: TiptapEditor, existingInstanceIds: Set<string>, blockId: string): BlockNodeWithPos | null {
  let result: BlockNodeWithPos | null = null;

  editor.state?.doc?.descendants((node, pos) => {
    if (node.type.name !== 'blockNode') return true;
    if (node.attrs.id !== blockId) return true;
    if (existingInstanceIds.has(node.attrs.instanceId)) return true;

    result = {
      node,
      startPos: pos,
      endPos: pos + node.nodeSize,
    };
    return false;
  });

  return result;
}
